import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Contact,
  ContactPage,
  CreateContactRequest,
  List,
  ImportResult,
} from '@mailflow/shared';
import { api, request, getActiveProjectId } from './api.js';

export interface ContactFilters {
  search?: string;
  status?: string;
  listId?: string;
  cursor?: string;
}

function toQueryString(filters: ContactFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const contactsQuery = (filters: ContactFilters) =>
  queryOptions({
    queryKey: ['contacts', filters],
    queryFn: ({ signal }) => api.get<ContactPage>(`/api/contacts${toQueryString(filters)}`, signal),
    staleTime: 10_000,
  });

export const contactQuery = (id: string) =>
  queryOptions({
    queryKey: ['contacts', 'detail', id],
    queryFn: ({ signal }) => api.get<{ contact: Contact }>(`/api/contacts/${id}`, signal),
  });

export const listsQuery = queryOptions({
  queryKey: ['lists'],
  queryFn: ({ signal }) => api.get<{ items: List[] }>('/api/lists', signal),
  staleTime: 30_000,
});

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactRequest) =>
      api.post<{ contact: Contact }>('/api/contacts', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useUpdateContact(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.put<{ contact: Contact }>(`/api/contacts/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/contacts/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useCreateList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      api.post<{ list: List }>('/api/lists', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['lists'] }),
  });
}

export function useRenameList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<{ list: List }>(`/api/lists/${id}`, { name }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['lists'] }),
  });
}

export function useDeleteList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/lists/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
      // Die Kontakte selbst bleiben, aber die Listenspalte der Ansicht nicht.
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// --- Massenbearbeitung ------------------------------------------------------

export interface BulkResult {
  affected: number;
  skipped: number;
}

export function useBulkList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; listId: string; action: 'add' | 'remove' | 'move' }) =>
      api.post<BulkResult>('/api/contacts/bulk/list', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

export function useBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<BulkResult>('/api/contacts/bulk/delete', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}

/**
 * Kontakte als CSV herunterladen — mit den Filtern der Ansicht.
 *
 * Ueber fetch statt ueber einen einfachen Link: der Kopf X-Project-Id muss
 * mit, sonst exportierte der Server das aelteste Projekt statt des
 * ausgewaehlten. Der Blattwechsel geschieht danach ueber ein kurzlebiges
 * Anker-Element — der einzige Weg, einen Dateinamen zu setzen.
 */
export async function downloadContactsCsv(filters: ContactFilters): Promise<number> {
  // Der Blaetter-Zeiger gehoert nicht in die Ausfuhr: exportiert wird alles,
  // was auf die Filter passt, nicht ab der aktuellen Seite.
  const { cursor: _cursor, ...ohneCursor } = filters;
  const projectId = getActiveProjectId();

  const response = await fetch(`/api/contacts/export.csv${toQueryString(ohneCursor)}`, {
    credentials: 'include',
    ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
  });

  if (!response.ok) {
    throw new Error(`Der Export ist fehlgeschlagen (HTTP ${response.status}).`);
  }

  const blob = await response.blob();
  const name =
    /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
    'kontakte.csv';

  const url = URL.createObjectURL(blob);
  const anker = document.createElement('a');
  anker.href = url;
  anker.download = name;
  document.body.appendChild(anker);
  anker.click();
  anker.remove();
  URL.revokeObjectURL(url);

  return blob.size;
}

// --- Import -----------------------------------------------------------------

export interface UploadResponse {
  uploadId: string;
  filename: string;
  size: number;
  /** Woraus die Vorschau entstanden ist: CSV oder umgewandelte Excel-Mappe. */
  source: 'csv' | 'xlsx';
  /** Bei Excel: das gelesene Blatt. */
  sheetName: string | null;
  preview: {
    headers: string[];
    sampleRows: string[][];
    totalRowsEstimate: number;
    delimiter: string;
    mapping: Array<{ column: string; field: string; inferred: boolean }>;
  };
}

/**
 * Datei-Upload laeuft ueber FormData, nicht ueber den JSON-Client: der Browser
 * muss den Content-Type samt boundary selbst setzen.
 *
 * Das Projekt muss trotzdem mit. Ohne den Kopf nimmt der Server das aelteste
 * Projekt des Mandanten — bei mehreren Projekten waere die Datei damit im
 * falschen gelandet, waehrend Einfuegen und Adresse (ueber den JSON-Client)
 * im richtigen ankommen.
 */
export async function uploadImportFile(file: File): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file);

  const projectId = getActiveProjectId();

  const response = await fetch('/api/import/uploads', {
    method: 'POST',
    credentials: 'include',
    ...(projectId ? { headers: { 'X-Project-Id': projectId } } : {}),
    body: form,
  });

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (payload as { error?: { message?: string } } | null)?.error;
    throw new Error(error?.message ?? `Upload fehlgeschlagen (HTTP ${response.status}).`);
  }
  return payload as UploadResponse;
}

/** Aus der Zwischenablage eingefuegte Tabelle. */
export function importFromText(text: string, filename?: string): Promise<UploadResponse> {
  return api.post<UploadResponse>('/api/import/uploads/text', {
    text,
    ...(filename ? { filename } : {}),
  });
}

/** Von einer Adresse laden. Was der Server abrufen darf, entscheidet er selbst. */
export function importFromUrl(url: string): Promise<UploadResponse> {
  return api.post<UploadResponse>('/api/import/uploads/url', { url });
}

export function useRunImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      uploadId: string;
      mapping: Array<{ column: string; field: string }>;
      listId?: string | null;
      updateExisting: boolean;
      consentStatus: string;
    }) =>
      request<ImportResult>(`/api/import/uploads/${input.uploadId}/run`, {
        method: 'POST',
        body: {
          mapping: input.mapping,
          listId: input.listId ?? null,
          updateExisting: input.updateExisting,
          consentStatus: input.consentStatus,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });
}
